import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play, Instagram } from "lucide-react";

export interface BeholdPost {
  id: string;
  permalink?: string;
  caption?: string;
  mediaType?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  prunedCaption?: string;
  sizes?: {
    small?: { mediaUrl?: string };
    medium?: { mediaUrl?: string };
    large?: { mediaUrl?: string };
    full?: { mediaUrl?: string };
  };
}

interface Props {
  /** Behold feed ID, e.g. "aBcD1234" from https://feeds.behold.so/aBcD1234 */
  feedId: string;
  /** Max number of posts to display */
  limit?: number;
  className?: string;
}

const imageFor = (p: BeholdPost) =>
  p.sizes?.medium?.mediaUrl ||
  p.sizes?.large?.mediaUrl ||
  p.sizes?.small?.mediaUrl ||
  (p.mediaType?.toUpperCase() === "VIDEO" ? p.thumbnailUrl : p.mediaUrl) ||
  p.thumbnailUrl ||
  p.mediaUrl ||
  "";

/**
 * Native Instagram feed powered by Behold.so.
 * Fetches the public JSON feed (no iframe, no third-party branding)
 * and renders it as a horizontally scrolling gallery.
 */
export const BeholdFeed = ({ feedId, limit = 12, className = "" }: Props) => {
  const [posts, setPosts] = useState<BeholdPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!feedId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setFailed(false);
    fetch(`https://feeds.behold.so/${feedId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Behold feed ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!active) return;
        const list: BeholdPost[] = Array.isArray(data) ? data : data?.posts ?? [];
        setPosts(list.slice(0, limit));
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setFailed(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [feedId, limit]);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.max(240, el.clientWidth * 0.8);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${className}`}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="aspect-square bg-muted/40 animate-pulse" />
        ))}
      </div>
    );
  }

  if (failed || posts.length === 0) {
    return (
      <p className={`text-center text-sm text-muted-foreground font-light ${className}`}>
        Our latest posts are on Instagram.
      </p>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Arrows */}
      <button
        type="button"
        aria-label="Previous posts"
        onClick={() => scrollBy(-1)}
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 h-11 w-11 items-center justify-center border border-primary/40 bg-background/80 backdrop-blur-sm hover:bg-primary/10 hover:border-primary transition-all duration-500"
      >
        <ChevronLeft size={18} className="text-primary" />
      </button>
      <button
        type="button"
        aria-label="Next posts"
        onClick={() => scrollBy(1)}
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 h-11 w-11 items-center justify-center border border-primary/40 bg-background/80 backdrop-blur-sm hover:bg-primary/10 hover:border-primary transition-all duration-500"
      >
        <ChevronRight size={18} className="text-primary" />
      </button>

      <div
        ref={trackRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {posts.map((p) => {
          const src = imageFor(p);
          const isVideo = p.mediaType?.toUpperCase() === "VIDEO";
          return (
            <a
              key={p.id}
              href={p.permalink || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative shrink-0 snap-start w-[72%] sm:w-[46%] md:w-[31%] lg:w-[23%] aspect-square overflow-hidden border border-border/50 bg-muted/30"
            >
              {src ? (
                <img
                  src={src}
                  alt={p.prunedCaption || p.caption?.slice(0, 120) || "Instagram post"}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="h-full w-full bg-muted/40" />
              )}
              <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-colors duration-500" />
              {isVideo && (
                <span className="absolute top-3 right-3 h-7 w-7 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                  <Play size={12} className="text-primary fill-primary" />
                </span>
              )}
              <span className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <Instagram size={16} className="text-background drop-shadow" />
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default BeholdFeed;
